import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AuthContext } from '@e3dc-react/shell/Libs/authentication'
import { scanForHeatpumpWs } from 'src/Libs/flow/advanced-settings/heatpump/apis/scan-for-heatpump-ws'
import { logError } from 'src/Libs/flow/utils'
import { Alert } from '@e3dc-react/shell/Elements/Alert'

//#region Scanner
interface HeatpumpWebsocketDevice {
  mac: string
  deviceName: string
}

interface HeatpumpWebsocketMessage {
  results: { firstScanComplete: true; isPartialResult: false; devices: HeatpumpWebsocketDevice[] }
}

interface HeatpumpPairingScannerProps {
  installationId: number
  emcId: string
  currentPairingId: string
  setPairingId: (pairingId: string) => void
}

/**
 * Upon render it opens a websocket to initialize scanning for heatpump devices.
 * It renders the state and results of the scan, and allows selecting a result as the pairing id of the heatpump.
 */
export const HeatpumpPairingScanner: React.FC<HeatpumpPairingScannerProps> = ({ installationId, emcId, setPairingId, currentPairingId }) => {
  const { t } = useCustomTranslation()

  const [websocketState, setWebsocketState] = useState<'loading' | 'error' | 'idle'>('loading')
  const [websocketError, setWebsocketError] = useState<{
    statusCode: number
    message: string
  } | null>(null)
  const [websocketData, setWebsocketData] = useState<HeatpumpWebsocketDevice[]>([])
  const { authToken } = useContext(AuthContext)

  useEffect(() => {
    if (!authToken) return

    const socket = scanForHeatpumpWs(installationId, emcId, authToken)

    socket.onopen = () => setWebsocketState('loading')

    socket.onerror = (e: Event) => {
      logError('Heatpump websocket scan error:', e)
      setWebsocketState('error')
    }
    socket.onclose = () => setWebsocketState('error')

    socket.onmessage = (e: MessageEvent) => {
      const eventObject = JSON.parse(e.data)

      if (eventObject.statusCode === 204 && Object.keys(eventObject).length === 1) {
        setWebsocketState('idle')
        return
      }

      if (eventObject.statusCode === 408 || eventObject.statusCode === 400) {
        setWebsocketState('error')
        setWebsocketError({ statusCode: eventObject.statusCode, message: eventObject.error })
        return
      }

      setWebsocketState('idle')
      setWebsocketData((eventObject as HeatpumpWebsocketMessage)?.results?.devices ?? [])
    }

    return () => {
      socket.close()
    }
  }, [emcId, installationId, authToken])

  if (websocketState === 'loading') {
    return (
      <Box textAlign={'center'}>
        <CircularProgress />
      </Box>
    )
  }

  if (websocketState === 'error' && websocketError === null)
    return <Alert severity="error" text={t('installation.productEditor.scanNetwork.noResults')} />

  if (websocketState === 'error' && websocketError !== null) return <Alert severity="error" text={websocketError.message} />

  if (websocketState === 'idle' && websocketData.length === 0)
    return <Alert severity="error" text={t('installation.productEditor.scanNetwork.noResults')} />

  return (
    <Stack maxHeight={'500px'}>
      {websocketData.map(({ deviceName, mac }) => (
        <NetworkScanResultHeatpump key={mac} name={deviceName} mac={mac} handleSelect={() => setPairingId(mac)} selected={mac === currentPairingId} />
      ))}
    </Stack>
  )
}
//#endregion

//#region NetworkScanResultHeatpump component
interface NetworkScanResultHeatpumpProps {
  name: string
  mac: string
  handleSelect: () => void
  selected: boolean
}

const NetworkScanResultHeatpump: React.FC<NetworkScanResultHeatpumpProps> = ({ name, mac, handleSelect, selected }) => {
  const { t } = useCustomTranslation()

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: theme => `1px solid ${theme.palette.grey[500]}`,
        py: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'normal', sm: 'center' },
      }}
    >
      <Box>
        <Box display={'flex'}>
          <Typography sx={{ lineHeight: 1.5, minWidth: { xs: 60, sm: 100 } }}>{t('common.name')}</Typography>
          <PairingIdText>{name}</PairingIdText>
        </Box>
        <Box display={'flex'}>
          <Typography sx={{ lineHeight: 1.5, minWidth: { xs: 60, sm: 100 } }}>{t(`installation.productEditor.scanNetwork.mac`)}</Typography>
          <PairingIdText>{mac}</PairingIdText>
        </Box>
      </Box>
      <Button color={'secondary'} variant={'outlined'} size="large" onClick={handleSelect} disabled={selected}>
        {selected ? t('installation.productEditor.scanNetwork.selected') : t('installation.productEditor.scanNetwork.select')}
      </Button>
    </Box>
  )
}

const PairingIdText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontWeight: 'bold',
      lineHeight: 1.5,
      maxWidth: { xs: '80%', sm: undefined },
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    {children}
  </Typography>
)
//#endregion
